import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Theme, darken, lighten } from "@mui/material";
import { RootState } from "@store/store";
import { CompanyData } from "@interfaces/store";
import { configuraciones, resolveRailPosition, resolveStageIndex } from "./onboardingRail";

/**
 * Nombre a mostrar de la empresa/solicitud activa. Para PFAE la razón social no existe,
 * así que se arma con el nombre y apellidos de la persona física.
 */
export const getCompanyDisplayName = (company: CompanyData) => {
  if (company.is_pfae) {
    return [company.pfae_name, company.pfae_last_name, company.pfae_last_name_2]
      .filter((part) => part?.trim())
      .join(" ")
      .trim();
  }
  return (company.legal_name || company.name || "").trim();
};

export const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");

/** Ancho del sidebar de escritorio y del menú lateral en móvil. */
export const SIDEBAR_WIDTH = 260;

/** Alto del logo en el sidebar: intermedio entre los 22px de `.sb-logo img` del diseño y el tamaño del navbar legacy. */
export const SIDEBAR_LOGO_HEIGHT = 40;

/** Blanco translúcido de `.sb-dark` del diseño, por nivel de énfasis. */
export const white = (opacity: number) => `rgba(255,255,255,${opacity})`;

/**
 * Colores de `.sb-dark` del diseño (override de Flux), derivados de la paleta del tenant:
 * - `bg`: `primary.main` oscurecido (`#253F3A` → `#172D29` en Flux);
 * - `mark`: `primary.main` aclarado para los números y checks del rail (`#3A5C54`);
 * - `dot`: `primary.accent` aclarado para el punto de la fase activa (`#B5CEAF`).
 */
export const getSidebarColors = (theme: Theme) => {
  const { main, accent } = theme.palette.primary;
  const mark = lighten(main, 0.12);
  return {
    bg: darken(main, 0.38),
    mark,
    markSoft: `color-mix(in srgb, ${mark} 24%, transparent)`,
    markRing: `color-mix(in srgb, ${mark} 28%, transparent)`,
    activeBg: `color-mix(in srgb, ${mark} 20%, transparent)`,
    dot: lighten(accent, 0.3),
    avatar: `linear-gradient(135deg, ${mark} 0%, ${main} 100%)`,
  };
};

/** Botón "Iniciar sesión" sobre el fondo oscuro: contorno blanco translúcido. */
export const loginButtonSx = {
  textTransform: "none",
  fontFamily: (theme: Theme) => theme.typography.fontFamily,
  fontWeight: 600,
  color: "#fff",
  borderColor: white(0.28),
  borderRadius: "8px",
  "&:hover": { borderColor: white(0.5), bgcolor: white(0.08) },
} as const;

/**
 * Posición actual del rail (grupo y fase activos), compartida por el sidebar de escritorio y el
 * navbar móvil. Antes de que exista solicitud no hay status, así que cae al primer grupo/fase.
 */
export const useTofuRail = () => {
  const { pathname } = useLocation();
  const loanStatus = useSelector((state: RootState) => state.register.loan_status);
  const statusConfig = useSelector((state: RootState) => state.app.status_config);

  // Hoy solo está definido PM; PFAE recorre la misma secuencia de fases.
  const groups = configuraciones.PM;
  // Etapa: por `RAIL_STAGE_STARTS` / `RAIL_STAGE_ENDS` sobre el orden del `status_config`.
  const activeGroupIndex = resolveStageIndex(
    groups,
    statusConfig.map((item) => item.code),
    loanStatus,
    pathname,
  );
  // Fase dentro de la etapa (hoy oculta en el rail; ver `SHOW_RAIL_PHASES`).
  const { groupIndex, phaseIndex } = resolveRailPosition(groups, loanStatus, pathname);
  const activeIndex = groupIndex === activeGroupIndex && phaseIndex !== -1 ? phaseIndex : 0;
  const phases = groups[activeGroupIndex].phases;

  return {
    groups,
    activeGroupIndex,
    activeIndex,
    phases,
    activePhase: phases[activeIndex],
    activeGroup: groups[activeGroupIndex],
    /** Fases completadas + la actual, sobre las fases del grupo activo. */
    progress: phases.length ? ((activeIndex + 1) / phases.length) * 100 : 0,
    /** Etapas completadas + la actual, sobre las etapas (Onboarding, Expediente, Firma). */
    groupProgress: groups.length ? ((activeGroupIndex + 1) / groups.length) * 100 : 0,
  };
};
